/* eslint-disable no-template-curly-in-string */
import { SLIDER_MAX_VAL, SLIDER_MIN_VAL } from '@constants/common';

import AutocompleteSearchFilter from '@/shared/SearchFilters/AutocompleteSearchFilter';
import CheckboxSearchFilter from '@/shared/SearchFilters/CheckboxSearchFilter';
import DropdownSearchFilter from '@/shared/SearchFilters/DropdownSearchFilter';
import {
  JobshiftOptions,
  JobtypeOptions,
  PropertyOptions,
  PropertyStatusOptions,
  PropertyTypeOptions,
  SqftOptions,
} from '@/shared/SearchFilters/options';
import SliderSearchFilter from '@/shared/SearchFilters/SliderSearchFilter';
import TextFieldSearchFilter from '@/shared/SearchFilters/TextFieldSearchFilter';

// Filter options response type from API
interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterOptionsResponse {
  sources?: FilterOption[];
  categories?: FilterOption[];
  positions?: FilterOption[];
  industries?: FilterOption[];
  ratings?: FilterOption[];
  states?: FilterOption[];
  employee_ranges?: FilterOption[];
  stats?: {
    total?: number;
    has_phone?: number;
    has_email?: number;
    has_website?: number;
    has_linkedin?: number;
    has_additional_contacts?: number;
    has_rating?: number;
    has_personal_email?: number;
    has_headline?: number;
    has_employee_count?: number;
  };
}

// Create filters for Scrapeking database with dynamic options from API
export const createScrapekingDbFilters = (filterOptions?: FilterOptionsResponse) => {
  const categoryOptions =
    filterOptions?.categories?.slice(0, 50).map(c => ({
      label: `${c.label} (${c.count?.toLocaleString() || 0})`,
      value: c.value,
    })) || [];

  // Position options for filtering by title (CEO, CTO, Manager, etc.)
  const positionOptions =
    filterOptions?.positions?.slice(0, 30).map(p => ({
      label: `${p.label} (${p.count?.toLocaleString() || 0})`,
      value: p.value,
    })) || [];

  // Industry options for filtering by business industry
  const industryOptions =
    filterOptions?.industries?.slice(0, 50).map(i => ({
      label: `${i.label} (${i.count?.toLocaleString() || 0})`,
      value: i.value,
    })) || [];

  const ratingOptions = [
    { label: '4+ stars', value: '4' },
    { label: '3+ stars', value: '3' },
    { label: '2+ stars', value: '2' },
    { label: '1+ stars', value: '1' },
  ];

  const qualityOptions = [
    { label: '8+ (High)', value: '8' },
    { label: '6+ (Medium)', value: '6' },
    { label: '4+ (Low)', value: '4' },
    { label: '2+ (Any)', value: '2' },
  ];

  const emailSourceOptions = [
    { label: 'Scraped from website', value: 'scraped' },
    { label: 'Guessed (info@domain)', value: 'guessed' },
  ];

  // State dropdown from API
  const stateOptions =
    filterOptions?.states?.slice(0, 50).map(s => ({
      label: `${s.label} (${s.count?.toLocaleString() || 0})`,
      value: s.value,
    })) || [];

  // Employee count range options
  const employeeRangeOptions = filterOptions?.employee_ranges?.map(e => ({
    label: e.label,
    value: e.value,
  })) || [
    { label: '1-10 employees', value: '1-10' },
    { label: '11-50 employees', value: '11-50' },
    { label: '51-200 employees', value: '51-200' },
    { label: '201-500 employees', value: '201-500' },
  ];

  const stats = filterOptions?.stats;
  const phoneCount = stats?.has_phone?.toLocaleString() || '0';
  const emailCount = stats?.has_email?.toLocaleString() || '0';
  const websiteCount = stats?.has_website?.toLocaleString() || '0';
  const linkedinCount = stats?.has_linkedin?.toLocaleString() || '0';
  const additionalContactsCount = stats?.has_additional_contacts?.toLocaleString() || '0';
  const personalEmailCount = stats?.has_personal_email?.toLocaleString() || '0';

  return [
    {
      field: 'search',
      Component: TextFieldSearchFilter,
      label: 'Search (name, address, phone)',
      type: 'text',
    },
    {
      field: 'company',
      Component: TextFieldSearchFilter,
      label: 'Company',
      type: 'text',
    },
    {
      field: 'position',
      Component: AutocompleteSearchFilter,
      label: 'Position',
      options: positionOptions,
    },
    {
      field: 'headline',
      Component: TextFieldSearchFilter,
      label: 'Position Search',
      type: 'text',
    },
    {
      field: 'category',
      Component: AutocompleteSearchFilter,
      label: 'Category',
      options: categoryOptions,
    },
    {
      field: 'industry',
      Component: AutocompleteSearchFilter,
      label: 'Industry',
      options: industryOptions,
    },
    {
      field: 'state',
      Component: AutocompleteSearchFilter,
      label: 'State',
      options: stateOptions,
    },
    {
      field: 'location',
      Component: TextFieldSearchFilter,
      label: 'Location',
      type: 'text',
    },
    {
      field: 'max_employee_count',
      Component: DropdownSearchFilter,
      label: 'Company Size',
      options: employeeRangeOptions,
    },
    {
      field: 'min_quality',
      Component: DropdownSearchFilter,
      label: 'Min Quality Score',
      options: qualityOptions,
    },
    {
      field: 'min_rating',
      Component: DropdownSearchFilter,
      label: 'Min Rating',
      options: ratingOptions,
    },
    {
      field: 'email_source',
      Component: DropdownSearchFilter,
      label: 'Email Source',
      options: emailSourceOptions,
    },
    {
      field: 'has_phone',
      Component: CheckboxSearchFilter,
      label: `Has Phone (${phoneCount})`,
    },
    {
      field: 'has_email',
      Component: CheckboxSearchFilter,
      label: `Has Email (${emailCount})`,
    },
    {
      field: 'has_personal_email',
      Component: CheckboxSearchFilter,
      label: `Has Personal Email (${personalEmailCount})`,
    },
    {
      field: 'has_website',
      Component: CheckboxSearchFilter,
      label: `Has Website (${websiteCount})`,
    },
    {
      field: 'has_linkedin',
      Component: CheckboxSearchFilter,
      label: `Has LinkedIn (${linkedinCount})`,
    },
    {
      field: 'has_additional_contacts',
      Component: CheckboxSearchFilter,
      label: `Has Extra Contacts (${additionalContactsCount})`,
    },
  ];
};

export const createJobsFilters = () => [
  {
    field: 'data_filter_+string_contains+email',
    Component: TextFieldSearchFilter,
    label: 'Email',
  },
  {
    field: 'data_filter_+string_contains+phone',
    Component: TextFieldSearchFilter,
    label: 'Contact phone',
    type: 'number',
  },
  {
    field: 'data_filter_+string_contains+company_name',
    Component: TextFieldSearchFilter,
    label: 'Company',
  },
  {
    field: 'data_filter_+string_contains+company_location',
    Component: TextFieldSearchFilter,
    label: 'Company location',
  },
  {
    field: 'data_filter_+array_contains+job_type',
    Component: AutocompleteSearchFilter,
    label: 'Job type',
    type: 'text',
    options: JobtypeOptions,
  },
  {
    field: 'data_filter_+string_contains+job_title',
    Component: TextFieldSearchFilter,
    label: 'Job title',
  },
  {
    field: 'data_filter_+array_contains+job_shift',
    Component: AutocompleteSearchFilter,
    label: 'Job shift',
    type: 'text',
    options: JobshiftOptions,
  },
  {
    field: 'data_filter_+ne+vacancy_expired',
    Component: CheckboxSearchFilter,
    label: 'Vacant',
  },
  {
    field: 'data_filter_+string_contains+rating',
    Component: TextFieldSearchFilter,
    label: 'Rating',
    minValue: 0, // prevent TS error
    maxValue: 5, // prevent TS error
    dependencies: {}, // prevent TS error
  },
];

// createGoodsFilters removed - e-commerce doesn't produce lead contact data

export const createPropertyFilters = () => [
  {
    field: 'data_filter_+string_contains+address',
    Component: TextFieldSearchFilter,
    label: 'City',
    type: 'text', // prevent TS error
  },
  [
    {
      field: 'data_filter_+gte+price',
      Component: DropdownSearchFilter,
      label: 'Price Min',
      dependencies: {
        min: 'data_filter_+gte+price',
        max: 'data_filter_+lte+price',
      },
      options: PropertyOptions,
    },
    {
      field: 'data_filter_+lte+price',
      Component: DropdownSearchFilter,
      label: 'Price Max',
      dependencies: {
        min: 'data_filter_+gte+price',
        max: 'data_filter_+lte+price',
      },
      options: PropertyOptions,
    },
  ],
  {
    field: 'data_filter_+array_contains+property_type',
    Component: AutocompleteSearchFilter,
    label: 'Property type',
    options: PropertyTypeOptions,
  },
  {
    field: 'data_filter_+eq+bedrooms',
    Component: SliderSearchFilter,
    label: 'Beds',
    minValue: SLIDER_MIN_VAL,
    maxValue: SLIDER_MAX_VAL,
  },
  {
    field: 'data_filter_+eq+bathrooms',
    Component: SliderSearchFilter,
    label: 'Bathrooms',
    minValue: SLIDER_MIN_VAL,
    maxValue: SLIDER_MAX_VAL,
  },
  [
    {
      field: 'data_filter_+gte+sqft',
      Component: DropdownSearchFilter,
      label: 'Sqft Min',
      dependencies: {
        min: 'data_filter_+gte+sqft',
        max: 'data_filter_+lte+sqft',
      },
      options: SqftOptions,
    },
    {
      field: 'data_filter_+lte+sqft',
      Component: DropdownSearchFilter,
      label: 'Sqft Max',
      dependencies: {
        min: 'data_filter_+gte+sqft',
        max: 'data_filter_+lte+sqft',
      },
      options: SqftOptions,
    },
  ],
  {
    field: 'data_filter_+array_contains+status',
    Component: AutocompleteSearchFilter,
    label: 'Status',
    options: PropertyStatusOptions,
  },
  {
    field: 'data_filter_+string_contains+contact_phone',
    Component: TextFieldSearchFilter,
    label: 'Contact phone',
    type: 'number',
  },
];
